import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useStore} from "../../../hooks/useStore";
import {Col, Row, DatePicker, Card} from "antd";
import {useTranslation} from "react-i18next";
import {RangePickerContainer, Title, SelectField, FilterContainer} from "./orderReportPage.styles";
import {cities, timeRangeOptionsStatistics} from "../../../constants/constants";
import {arrayAlphabeticalSort, removeNullEntries} from "../../../utils/helpers";
import {useFetch} from "../../../hooks/useFetch";

import {OrderCountStatsWidget} from "./widgets/OrderCountStatsWidget/OrderCountStatsWidget";
import {OrderTimeStatsWidget} from "./widgets/OrderTimeStatsWidget/OrderTimeStatsWidget";
import {DurationStatsWidget} from "./widgets/DurationStatsWidget/DurationStatsWidget";
import {DailyOrderStatsWidget} from "./widgets/DailyOrderStatsWidget/DailyOrderStatsWidget";
import {HourlyOrderStatsWidget} from "./widgets/HourlyOrderStatsWidget/HourlyOrderStatsWidget";
import {PaymentMethodStatsWidget} from "./widgets/PaymentMethodStatsWidget/PaymentMethodStatsWidget";
import {OrderSourceStatsWidget} from "./widgets/OrderSourceStatsWidget/OrderSourceStatsWidget";
import {CityFilter, CompanyFilter} from "../../_defaultComponents/FilterComponents";
const {RangePicker} = DatePicker;

export const OrderReportPage = () => {
    const stores = useStore();
    const {t} = useTranslation(["orderReportPage", "timeRangeOptionsStatistics"]);
    const [city, setCity] = useState("all");
    const [company, setCompany] = useState("all");
    const [subCompany, setSubCompany] = useState("all");
    const [startDateOrderStatistics, setStartDateOrderStatistics] = useState("");
    const [endDateOrderStatistics, setEndDateOrderStatistics] = useState("");
    const [minuteHistoric, setMinuteHistoric] = useState(45);
    const [dateTypeForOrderStatistics, setDateTypeForOrderStatistics] = useState("monthly");
    const [dependencyCondition, setDependencyCondition] = useState(true);

    const selectFieldOnChange = value => {
        setDependencyCondition(true);
        setStartDateOrderStatistics("");
        setEndDateOrderStatistics("");
        setDateTypeForOrderStatistics(value);
        if (value === "choose") {
            setDependencyCondition(false);
        }
    };
    const rangePickerOnChange = formatString => {
        setDependencyCondition(true);
        setStartDateOrderStatistics(formatString[0]);
        setEndDateOrderStatistics(formatString[1]);
        setDependencyCondition(true);
    };

    const [
        companyOrderStatisticsLoading,
        companyOrderStatistics,
        setCompanyOrderStatistics,
        setCompanyOrderStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getCompanyOrderStatistics.bind(
            null,
            removeNullEntries({
                period: dateTypeForOrderStatistics,
                startDate: startDateOrderStatistics,
                endDate: endDateOrderStatistics,
                companyId: company,
                subCompanyId: subCompany,
                city: city
            })
        ),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            company,
            subCompany,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [
        companyOrderHourlyStatisticsLoading,
        companyOrderHourlyStatistics,
        setCompanyHourlyOrderStatistics,
        setCompanyHourlyOrderStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getCompanyOrderHourlyStatistics.bind(
            null,
            removeNullEntries({
                period: dateTypeForOrderStatistics,
                startDate: startDateOrderStatistics,
                endDate: endDateOrderStatistics,
                companyId: company,
                subCompanyId: subCompany,
                city: city
            })
        ),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            company,
            subCompany,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            limit: 1000
        }),
        dependencies: [company],
        initialCall: false
    });

    const [durationStatisticsLoading, durationStatistics] = useFetch({
        fetchFn: stores.statisticStore.getCompanyOrderDurationStatistics.bind(
            null,
            removeNullEntries({
                period: dateTypeForOrderStatistics,
                startDate: startDateOrderStatistics,
                endDate: endDateOrderStatistics,
                companyId: company,
                subCompanyId: subCompany,
                city: city,
                minute: minuteHistoric
            })
        ),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            company,
            subCompany,
            city,
            minuteHistoric
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    return useObserver(() => (
        <Row type="flex" justify="start" data-testid="orderReportScreen">
            <Title data-testid="companyName">
                {t("orderReportPageTitle", {ns: "orderReportPage"})}
            </Title>
            <Col span={24} data-testid="filterCard">
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                        <CompanyFilter
                            showAllOption={true}
                            allKey="all"
                            showPool={false}
                            value={company}
                            onChange={value => {
                                setCompany(value);
                                setSubCompany("all");
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                        <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                        <SelectField
                            data-testid="subCompanySelect"
                            value={subCompany}
                            showSearch
                            onChange={value => {
                                setSubCompany(value);
                            }}
                            disabled={!company}
                        >
                            <SelectField.Option value="all" data-testid="subCompanySelect_option">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {subCompanies.rows &&
                                subCompanies?.rows.map(subCompany => (
                                    <SelectField.Option
                                        key={subCompany._id}
                                        value={subCompany._id}
                                        data-testid="subCompanySelect_option"
                                    >
                                        {subCompany.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={12} xl={8}>
                        <h5>{t("fields.date", {ns: "common"})}</h5>
                        <Row>
                            {rangePickerOnChange ? (
                                <Col xs={24} sm={12} md={12} lg={12}>
                                    <SelectField
                                        data-testid="timeRangeSelect"
                                        value={dateTypeForOrderStatistics}
                                        onChange={value => {
                                            selectFieldOnChange(value);
                                        }}
                                    >
                                        {timeRangeOptionsStatistics.map((obj, i) => (
                                            <SelectField.Option
                                                key={i}
                                                data-testid="timeRangeSelect_option"
                                                value={obj.value}
                                            >
                                                {t(obj.translation_key, {
                                                    ns: "timeRangeOptionsStatistics"
                                                })}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </Col>
                            ) : null}
                            <Col span={24} xs={24} sm={12} md={12} lg={12}>
                                {dateTypeForOrderStatistics === "choose" && (
                                    <RangePickerContainer>
                                        <RangePicker
                                            data-testid="timeRangePicker"
                                            renderExtraFooter={() => {
                                                t("timeRangePickerPlaceholder", {ns: "common"});
                                            }}
                                            placeholder={[
                                                t("fields.startDate", {ns: "common"}),
                                                t("fields.endDate", {ns: "common"})
                                            ]}
                                            onChange={(values, formatString, info) => {
                                                rangePickerOnChange(formatString);
                                            }}
                                        />
                                    </RangePickerContainer>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </FilterContainer>
            </Col>
            <Col span={24}>
                <Row gutter={[16, 16]} style={{marginTop: "40px"}}>
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <OrderCountStatsWidget
                            data={companyOrderStatistics?.count}
                            loading={companyOrderStatisticsLoading}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <OrderTimeStatsWidget
                            data={companyOrderStatistics?.time}
                            loading={companyOrderStatisticsLoading}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <DurationStatsWidget
                            data={durationStatistics}
                            loading={durationStatisticsLoading}
                            duration={minuteHistoric}
                            onChangeDuration={setMinuteHistoric}
                        />
                    </Col>
                </Row>
            </Col>
            <Col span={24}>
                <Row gutter={[16, 16]} style={{marginTop: "20px"}}>
                    <Col xs={24} sm={24} md={12}>
                        <DailyOrderStatsWidget
                            data={companyOrderStatistics?.count?.daily}
                            loading={companyOrderStatisticsLoading}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <HourlyOrderStatsWidget
                            data={companyOrderHourlyStatistics?.hourlyCount}
                            loading={companyOrderHourlyStatisticsLoading}
                        />
                    </Col>
                </Row>
            </Col>
            <Col span={24} style={{marginTop: "20px"}}>
                <Card>
                    <Row>
                        <Col xs={24} sm={24} md={12}>
                            <h1>
                                {t("totalOrderPrice", {ns: "orderReportPage"})}:{" "}
                                <b style={{color: "#FA7880"}}>
                                    {companyOrderStatistics?.price?.total.toLocaleString("tr-TR") ||
                                        "-"}{" "}
                                    ₺
                                </b>
                            </h1>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <h1>
                                {t("averageOrderPrice", {ns: "orderReportPage"})}:{" "}
                                <b style={{color: "#FA7880"}}>
                                    {companyOrderStatistics?.price?.average.toLocaleString(
                                        "tr-TR"
                                    ) || "-"}{" "}
                                    ₺
                                </b>
                            </h1>
                        </Col>
                    </Row>
                </Card>
            </Col>
            <Col span={24}>
                <Row gutter={[16, 16]} style={{marginTop: "20px"}}>
                    <Col xs={24} sm={24} md={12}>
                        <PaymentMethodStatsWidget
                            data={companyOrderStatistics?.paymentType}
                            loading={companyOrderStatisticsLoading}
                        />
                    </Col>
                    {/*   <Col xs={24} sm={24} md={12}>
                        <OrderSourceStatsWidget
                            data={companyOrderStatistics?.source}
                            loading={companyOrderStatisticsLoading}
                        />
                    </Col> */}
                </Row>
            </Col>
        </Row>
    ));
};
